'use client';

import { useEffect, useState } from 'react';

// Utils
import { combineClasses } from '@shared/utils';

// Constants
import { ERROR_MESSAGES, YEAR_IN_REVIEW } from '@/constants';

// Providers
import { useFirebase } from '@/providers/firebase';

// Actions
import { getEmployeeYearInReviewVideo } from '@/actions/client/employee';
interface YearInReviewVideoPlayerProps {
  identity: string;
  year: number;
  title?: string;
  className?: string;
}
export const YearInReviewVideoPlayer = ({
  identity,
  year,
  title = `${YEAR_IN_REVIEW.LABEL} Video`,
  className
}: YearInReviewVideoPlayerProps) => {
  const [videoBlobUrl, setVideoBlobUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const {
    getToken
  } = useFirebase();
  useEffect(() => {
    let isMounted = true;
    const fetchVideo = async (): Promise<void> => {
      const {
        TOKEN_NOT_AVAILABLE,
        FETCH_VIDEOS_FAILED
      } = ERROR_MESSAGES;
      try {
        if (!isMounted) return;
        setIsLoading(true);
        setError('');
        const token = await getToken();
        if (!isMounted) return;
        if (!token) {
          setError(TOKEN_NOT_AVAILABLE);
          setIsLoading(false);
          return;
        }
        const response = await getEmployeeYearInReviewVideo(identity, year, {
          requestConfig: {
            token
          }
        });
        if (!isMounted) return;
        if (response.error || !response.data) {
          setError(typeof response.error === 'string' ? response.error : FETCH_VIDEOS_FAILED);
          setIsLoading(false);
          return;
        }
        setVideoBlobUrl(response.data);
        setIsLoading(false);
      } catch (err) {
        if (!isMounted) return;
        const errorMessage = err instanceof Error ? err.message : FETCH_VIDEOS_FAILED;
        setError(errorMessage);
        setIsLoading(false);
      }
    };
    fetchVideo();

    // Cleanup function to revoke the blob URL and cancel async operations
    return () => {
      isMounted = false;
      if (videoBlobUrl) {
        URL.revokeObjectURL(videoBlobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, year, getToken]);
  return <div role="region" aria-label={`${title} player`} className={combineClasses('relative bg-black aspect-video w-full', className)} data-testid="year-in-review-player-container" data-sentry-component="YearInReviewVideoPlayer" data-sentry-source-file="index.tsx">
      {isLoading && <div className="flex items-center justify-center w-full h-full" data-testid="year-in-review-loading">
          <p className="text-white">Loading video...</p>
        </div>}

      {error && <div className="flex items-center justify-center w-full h-full" data-testid="year-in-review-error">
          <p role="alert" className="text-white p-4">
            {error}
          </p>
        </div>}

      {!isLoading && !error && videoBlobUrl && <video className="w-full h-full" controls controlsList="nodownload" preload="auto" playsInline autoPlay aria-label={title} data-testid="year-in-review-player-video">
          <source src={videoBlobUrl} type="video/mp4" data-testid="year-in-review-video-source" />
          <track kind="captions" srcLang="en" label="English" data-testid="year-in-review-video-track" />
          <p role="alert" className="text-white p-4">
            Your browser does not support the video tag. Please update your
            browser or try a different one.
          </p>
        </video>}
    </div>;
};